import { Document, Page, Text, View, Image } from "@react-pdf/renderer";
import { styles } from "./reportStyles";
import { ClinicInfo } from "../../utils/makeClinicInfo";
import { TechnicianInfo } from "../../utils/makeTechnicianInfo";
import { PdfTechnicianSignature } from "../pdf/document-components/PdfTechnicianSignature";
import { PdfFooter } from "../pdf/document-components/PdfFooter";
import { PdfHeader } from "../pdf/document-components/PdfHeader";
import { HtmlToPdfElements } from "../pdf/htmlToPdfElements";
import { EegDocumentAppendixView } from "./EegDocumentAppendixView";

export type ReportData = {
	patientName: string;
	birthDate: string;
	age: string;
	evaluationDate: string;
	medication: string;
	laterality: string;
	evaluationMotivation: string;
	medicalPsychologicalHistory: string;
	doneSessions: string;
	therapeuticGoals: string;
	evaluationsTests: Array<string>;
	colaboration: string;
	resultsInterpertation: string;
	eyesOpen: {
		incidenciaAbsoluta: string;
		conectividade: string;
		images: Array<string>;
	};
	eyesClosed: {
		incidenciaAbsoluta: string;
		conectividade: string;
		images: Array<string>;
	};
	observations: string;
	signatureLocationDate: string;
	technician: TechnicianInfo | null;
	prescriberTechnician: TechnicianInfo | null;
	otherImages: Array<string>;
	cbclImages: Array<string>;
	d2Images: Array<string>;
	d2Title: string;
	bdiImages: Array<string>;
	bsiImages: Array<string>;
	asrsImages: Array<string>;
	clinics: Array<ClinicInfo>;
	isFirstEvaluation: boolean;
};
type MyDocumentProps = {
	reportData: ReportData;
};

export const EegDocument = ({ reportData }: MyDocumentProps) => {
	const hasCbcl = reportData.cbclImages.length > 0;
	const hasD2 = reportData.d2Images.length > 0;
	const hasAsrs = reportData.asrsImages.length > 0;
	const hasBsi = reportData.bsiImages.length > 0;
	const hasBdi = reportData.bdiImages.length > 0;

	let instrumentCounter = 0;
	const instrumentIndex = {
		cbcl: hasCbcl ? ++instrumentCounter : undefined,
		d2: hasD2 ? ++instrumentCounter : undefined,
		asrs: hasAsrs ? ++instrumentCounter : undefined,
		bsi: hasBsi ? ++instrumentCounter : undefined,
		bdi: hasBdi ? ++instrumentCounter : undefined,
	};

	const reportTitle = reportData.isFirstEvaluation ? "Relatório de Avaliação" : "Relatório de Re-avaliação";
	return (
		<Document>
			<Page size="A4" style={styles.page}>
				<PdfHeader />
				<View style={[styles.contentWrapper, {flex:1, justifyContent: "space-between" }]}>
					<View>
						<View style={styles.titleContainer}>
							<Text style={styles.title}>{reportTitle} com</Text>
							<Text style={styles.title}>Eletroencefalografia Quantitativa</Text>
						</View>
						<View style={styles.tableContainer}>
							<View style={styles.topSection}>
								<View style={styles.tableRow}>
									<Text style={styles.tableLabel}>Nome:</Text>
									<Text style={styles.tableContent}>{reportData.patientName}</Text>
								</View>
								<View style={styles.tableRow}>
									<Text style={styles.tableLabel}>Data de Nascimento:</Text>
									<Text
										style={styles.tableContent}
									>{`${reportData.birthDate} (${reportData.age} anos)`}</Text>
								</View>
								<View style={styles.tableRow}>
									<Text style={styles.tableLabel}>Data da Avaliação:</Text>
									<Text style={styles.tableContent}>{reportData.evaluationDate}</Text>
								</View>
								<View style={styles.tableRow}>
									<Text style={styles.tableLabel}>Medicação no ato do exame:</Text>
									<Text style={styles.tableContent}>{reportData.medication}</Text>
								</View>
								<View style={styles.tableRow}>
									<Text style={styles.tableLabel}>Lateralidade:</Text>
									<Text style={styles.tableContent}>{reportData.laterality}</Text>
								</View>

								<View style={styles.otherFields}>
									{reportData.isFirstEvaluation && (
										<View style={{ gap: 10 }}>
											<View style={styles.tableRow}>
												<Text style={styles.tableLabel}>Motivo da avaliação:</Text>
												<Text style={styles.tableContent}>
													{reportData.evaluationMotivation}
												</Text>
											</View>
											<View style={styles.tableRow}>
												<Text style={styles.tableLabel}>Historial médico e psicológico:</Text>
												<Text style={styles.tableContent}>
													{reportData.medicalPsychologicalHistory}
												</Text>
											</View>
										</View>
									)}
									{!reportData.isFirstEvaluation && (
										<View style={{ gap: 10 }}>
											<View style={styles.tableRow}>
												<Text style={styles.tableLabel}>Número de sessões realizadas:</Text>
												<Text style={styles.tableContent}>{reportData.doneSessions}</Text>
											</View>
											<View style={styles.tableRow}>
												<Text style={styles.tableLabel}>Objectivos terapêuticos à data:</Text>
												<Text style={styles.tableContent}>{reportData.therapeuticGoals}</Text>
											</View>
										</View>
									)}
									<View style={styles.tableRow}>
										<Text style={styles.tableLabel}>Técnicas de EEG:</Text>

										<Text style={styles.tableContent}>
											EEG de 19 canais; Sistema 10-20; Intervalo de frequências: 1-30 Hz;
											Montagem: Linked Ear; Artefactos não considerados na análise; Condições de
											registo: Olhos abertos e fechados, cliente sentado.
										</Text>
									</View>
									<View style={styles.tableRow}>
										<Text style={styles.tableLabel}>Instrumentos de EEG:</Text>

										<Text style={styles.tableContent}>
											BrainAvatar System (BrainMaster Technology Inc.);{"\n"}
											Neuroguide database (Applied Neuroscience Inc.).
										</Text>
									</View>
									<View style={styles.tableRow}>
										<Text style={styles.tableLabel}>
											Provas de avaliação{"\n"}psicológica aplicadas:
										</Text>
										<Text style={styles.tableContent}>
											{reportData.evaluationsTests.join(";\n") + "."}
										</Text>
									</View>
									<View style={styles.tableRow}>
										<Text style={styles.tableLabel}>Colaboração:</Text>
										<Text style={styles.tableContent}>{reportData.colaboration}</Text>
									</View>
								</View>
							</View>
						</View>
					</View>
					<View wrap={false}>
						<Text style={[styles.text, { textAlign: "right" }]}>{reportData.signatureLocationDate}</Text>
						<View
							style={{
								flexDirection: "row",
								justifyContent: "center",
								alignItems: "flex-end",
							}}
						>
							{reportData.prescriberTechnician && (
								<PdfTechnicianSignature
									technician={reportData.prescriberTechnician}
									variation="inline"
								/>
							)}
							{reportData.technician && (
								<PdfTechnicianSignature technician={reportData.technician} variation="inline" />
							)}
						</View>
					</View>
				</View>
				<PdfFooter clinics={reportData.clinics} />
			</Page>
			<Page size="A4" style={styles.page}>
				<PdfHeader mb={30} />
				<View style={styles.contentWrapper}>
					<View style={{ marginBottom: 20 }}>
						<Text style={[styles.sectionTitle, { marginBottom: 16 }]}>Interpretação dos resultados</Text>
						<HtmlToPdfElements
							html={reportData.resultsInterpertation}
							textStyle={styles.textSpacedJustified}
						/>
					</View>

					{reportData.observations && (
						<View wrap={false}>
							<Text style={styles.sectionSubTitle}>Observações</Text>
							<Text style={styles.textSpacedJustified}>{reportData.observations}</Text>
						</View>
					)}
				</View>
				<PdfFooter clinics={reportData.clinics} />
			</Page>

			{(hasCbcl || hasD2 || hasBdi || hasBsi || hasAsrs) && (
				<Page size="A4" style={styles.page}>
					<PdfHeader mb={30} />
					<View style={styles.contentWrapper}>
						<Text style={[styles.sectionTitle]}>Resultados das provas de avaliação psicológica</Text>
						{hasCbcl &&
							reportData.cbclImages.map((image, index) => (
								<View key={`cbcl-${index}`} wrap={false} style={index === reportData.cbclImages.length - 1 && instrumentIndex.cbcl! < instrumentCounter ? { marginBottom: 20 } : undefined}>
									{index === 0 && (
										<Text style={[styles.textBold, { marginBottom: 5 }]}>
											Tabela {instrumentIndex.cbcl}. CBCL (Child Behavior Checklist)
										</Text>
									)}
									<Image
										style={{ width: "100%", height: "auto", objectFit: "contain" }}
										src={image}
										cache={false}
									/>
								</View>
							))}
						{hasD2 &&
							reportData.d2Images.map((image, index) => (
								<View key={`d2-${index}`} wrap={false} style={index === reportData.d2Images.length - 1 && instrumentIndex.d2! < instrumentCounter ? { marginBottom: 20 } : undefined}>
									{index === 0 && (
										<Text
											style={[styles.textBold, { marginBottom: 5 }]}
										>
											Tabela {instrumentIndex.d2}. {reportData.d2Title}
										</Text>
									)}
									<Image
										style={{ width: "100%", height: "auto", objectFit: "contain" }}
										src={image}
										cache={false}
									/>
								</View>
							))}
						{hasAsrs &&
							reportData.asrsImages.map((image, index) => (
								<View key={`asrs-${index}`} wrap={false} style={index === reportData.asrsImages.length - 1 && instrumentIndex.asrs! < instrumentCounter ? { marginBottom: 20 } : undefined}>
									{index === 0 && (
										<Text
											style={[styles.textBold, { marginBottom: 5 }]}
										>
											Tabela {instrumentIndex.asrs}. ASRS-18 (Escala de auto-avaliação de PHDA para o adulto)
										</Text>
									)}
									<Image
										style={{ width: "100%", height: "auto", objectFit: "contain" }}
										src={image}
										cache={false}
									/>
								</View>
							))}
						{hasBsi &&
							reportData.bsiImages.map((image, index) => (
								<View key={`bsi-${index}`} wrap={false} style={index === reportData.bsiImages.length - 1 && instrumentIndex.bsi! < instrumentCounter ? { marginBottom: 20 } : undefined}>
									{index === 0 && (
										<Text
											style={[styles.textBold, { marginBottom: 5 }]}
										>
											Tabela {instrumentIndex.bsi}. BSI (Inventário de Sintomas Psicopatológicos)
										</Text>
									)}
									<Image
										style={{ width: "100%", height: "auto", objectFit: "contain" }}
										src={image}
										cache={false}
									/>
								</View>
							))}
						{hasBdi &&
							reportData.bdiImages.map((image, index) => (
								<View key={`bdi-${index}`} wrap={false} style={index === reportData.bdiImages.length - 1 && instrumentIndex.bdi! < instrumentCounter ? { marginBottom: 20 } : undefined}>
									{index === 0 && (
										<Text
											style={[styles.textBold, { marginBottom: 5 }]}
										>
											Tabela {instrumentIndex.bdi}. BDI (Inventário Depressivo de Beck)
										</Text>
									)}
									<Image
										style={{ width: "100%", height: "auto", objectFit: "contain" }}
										src={image}
										cache={false}
									/>
								</View>
							))}
					</View>
					<PdfFooter clinics={reportData.clinics} />
				</Page>
			)}

			{reportData.eyesOpen.images.map((image, index) => (
				<Page key={`open-${index}`} size="A4" style={styles.page}>
					<PdfHeader mb={30} />
					<View style={styles.contentWrapper}>
						{index === 0 && <Text style={[styles.sectionTitle, styles.centeredText]}>Olhos Abertos:</Text>}
						<Image style={styles.brainImage} src={image} cache={false} />
					</View>
					<PdfFooter clinics={reportData.clinics} />
				</Page>
			))}

			{reportData.eyesClosed.images.map((image, index) => (
				<Page key={`closed-${index}`} size="A4" style={styles.page}>
					<PdfHeader mb={30} />
					<View style={styles.contentWrapper}>
						{index === 0 && <Text style={[styles.sectionTitle, styles.centeredText]}>Olhos Fechados:</Text>}
						<Image style={styles.brainImage} src={image} cache={false} />
					</View>
					<PdfFooter clinics={reportData.clinics} />
				</Page>
			))}

			{reportData.otherImages.map((image, index) => (
				<Page key={`other-${index}`} size="A4" style={styles.page}>
					<PdfHeader mb={30} />
					<View style={styles.contentWrapper}>
						{index === 0 && (
							<Text style={[styles.sectionTitle, styles.centeredText]}>Imagens Adicionais:</Text>
						)}
						<Image style={styles.brainImage} src={image} cache={false} />
					</View>
					<PdfFooter clinics={reportData.clinics} />
				</Page>
			))}
			<Page size="A4" style={styles.page}>
				<PdfHeader mb={30} />
				<View style={styles.contentWrapper}>
					<EegDocumentAppendixView />
				</View>
				<PdfFooter clinics={reportData.clinics} />
			</Page>
		</Document>
	);
};
